import React, { useState, useEffect } from "react"
import clsx from "clsx"
import { Grid, Typography, Box } from "@material-ui/core"

import { SparkleSingleIcon } from "@/assets/icons"

import { SvgIcon, Loading } from "@/components"

import { ErrorType } from "@/hooks/useValidation"

import AttendanceProtocolSummaryService from "@/services/AttendanceProtocolSummary"
import ErrorHandlerService from "@/services/ErrorHandler"

import AttendanceSummaryModalSkeleton from "@/skeletons/AttendanceSummaryModal"

import { colors } from "@/styles/colors"

import useStyles from "@/components/Skynet/AttendanceSummary/AssignAttendance/AttendanceSummaryPreview/styles"

const SUMMARY_PREVIEW_FALLBACK_MESSAGE = "Não foi possível gerar a prévia do resumo."
const SUMMARY_PREVIEW_LABEL = "Prévia do resumo com IA"

type AttendanceSummaryPreviewProps = {
	isSummaryIncluded: boolean
	inboxChannelChatAttendanceId: number
}

const AttendanceSummaryPreview: React.FC<AttendanceSummaryPreviewProps> = (props) => {
	const {
		isSummaryIncluded,
		inboxChannelChatAttendanceId
	} = props

	const classes = useStyles()

	const [summaryPreview, setSummaryPreview] = useState<string>("")
	const [isSummaryPreviewLoading, setIsSummaryPreviewLoading] = useState<boolean>(false)
	const [hasGeneratedPreview, setHasGeneratedPreview] = useState<boolean>(false)

	const generatePreview = async () => {
		if (hasGeneratedPreview) {
			return
		}

		setIsSummaryPreviewLoading(true)
		setSummaryPreview("")

		if (!inboxChannelChatAttendanceId) {
			setIsSummaryPreviewLoading(false)
			setSummaryPreview(SUMMARY_PREVIEW_FALLBACK_MESSAGE)
			setHasGeneratedPreview(true)
			return
		}

		try {
			await AttendanceProtocolSummaryService.streamSummary(inboxChannelChatAttendanceId, {
				forceRegenerate: true,
				onTextChunk: (textChunk) => {
					setSummaryPreview(text => text + textChunk)
				}
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			setSummaryPreview(SUMMARY_PREVIEW_FALLBACK_MESSAGE)
		}

		setIsSummaryPreviewLoading(false)
		setHasGeneratedPreview(true)
	}

	useEffect(() => {
		if (!isSummaryIncluded) {
			return
		}

		generatePreview()
	}, [isSummaryIncluded])

	return (
		<Box
			className={clsx({
				[classes.summaryPreviewCard]: true,
				[classes.hidden]: !isSummaryIncluded
			})}
		>
			<Grid
				container
				direction="column"
			>
				<Grid
					item
					container
					alignItems="center"
					spacing={1}
				>
					<Grid item>
						<SvgIcon
							icon={SparkleSingleIcon}
							htmlColor={colors.purple[600]}
							autoSize
							className={classes.sparklesIcon}
						/>
					</Grid>
					<Grid item>
						<Typography className={classes.summaryPreviewLabel}>
							{SUMMARY_PREVIEW_LABEL}
						</Typography>
					</Grid>
				</Grid>
				<Grid item>
					<Loading
						loading={isSummaryPreviewLoading && !summaryPreview}
						customLoadingElement={<AttendanceSummaryModalSkeleton />}
					>
						<Typography className={classes.summaryPreviewText}>
							{summaryPreview || SUMMARY_PREVIEW_FALLBACK_MESSAGE}
						</Typography>
					</Loading>
				</Grid>
			</Grid>
		</Box>
	)
}

export default AttendanceSummaryPreview
